import { useState, useMemo } from "react";

const useColumnFilters = (data = [], columns = []) => {
  const [columnFilters, setColumnFilters] = useState({});
  
  // Get unique values for each column
  const getUniqueValues = (field, valueGetter) => {
    const values = data.map((row) => {
      if (valueGetter) {
        return valueGetter(null, row);
      }
      return row[field] || "N/A";
    });

    return [...new Set(values)].sort((a, b) => {
      if (a === "N/A") return 1;
      if (b === "N/A") return -1;
      return String(a).localeCompare(String(b));
    });
  };

  // Filter data based on active column filters
  const filteredData = useMemo(() => {
    if (Object.keys(columnFilters).length === 0) {
      return data;
    }

    return data.filter((row) => {
      return Object.entries(columnFilters).every(([field, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) {
          return true; // No filter applied for this column
        }

        const column = columns.find((col) => col.field === field);
        let cellValue;

        if (column?.valueGetter) {
          cellValue = column.valueGetter(null, row);
        } else {
          cellValue = row[field] || "N/A";
        }

        return selectedValues.includes(cellValue);
      });
    });
  }, [data, columnFilters, columns]);

  const handleFilterChange = (field, selectedValues) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };

      if (!selectedValues || selectedValues.length === 0) {
        delete newFilters[field];
      } else {
        // Get all unique values for this field
        const column = columns.find((col) => col.field === field);
        const allUniqueValues = getUniqueValues(field, column?.valueGetter);

        // If all values are selected, remove the filter
        if (selectedValues.length === allUniqueValues.length) {
          delete newFilters[field];
        } else {
          newFilters[field] = selectedValues;
        }
      }

      return newFilters;
    });
  };

  const clearAllFilters = () => {
    setColumnFilters({});
  };

  const clearFilter = (field) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[field];
      return newFilters;
    });
  };

  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  return {
    filteredData,
    columnFilters,
    handleFilterChange,
    clearAllFilters,
    clearFilter,
    hasActiveFilters,
    getUniqueValues,
    // Expose the state setter for components that still call setColumnFilters directly
    setColumnFilters,
  };
};

export default useColumnFilters;
